import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useStorefrontConfig, useStorefrontHelper } from "src/storefront/types/storefront";
import { Category, CategoryBranch, CategoryLeaf } from "src/storefront/types/catalog";
import { NavigateCatalog, NavigatePage, StorefrontPage, useNavigationState } from "src/storefront/types/navigation";
import { classNames } from "src/common_util";
import { createPortal } from "react-dom";

export const CategoryBrowser = () => {
  const config = useStorefrontConfig();
  const { catalog } = useStorefrontHelper();

  const DURATION = 350;

  const [navigationState, updateNavigation] = useNavigationState();
  const focusedCategory = useMemo(() => {
    const component = catalog.findCatalogComponentFromKeyPath([...navigationState.focusedCatalogComponent]);

    if (component instanceof CategoryLeaf || component instanceof CategoryBranch)
      return component;
  }, [catalog, navigationState.focusedCatalogComponent]);

  const desktopContainer = document.querySelector("#nav-category-browser");
  const mobileBrowserRef = useRef<HTMLElement>(null);

  const navToCategory = useCallback((category: Category | undefined) => {
    updateNavigation(NavigateCatalog.action(category?.fullKeyPath() || []));
    updateNavigation(NavigatePage.action(StorefrontPage.Shop));
  }, [updateNavigation]);

  const expand = (detailsElement: HTMLDetailsElement, summaryElement: HTMLElement) => {
    // Apply a fixed height inline style to the <details> element so that it doesn't jump
    // when forced open in order to be able to calculate the open and closed dimensions.
    detailsElement.style.height = `${detailsElement.offsetHeight}px`;
    detailsElement.open = true;

    // Wait until the next frame before starting to expand
    window.requestAnimationFrame(() => {
      const detailsContent = detailsElement.querySelector('ul') as HTMLUListElement;

      // Get the current startHeight of the <details> element, then calculate the endHeight
      // as if it were expanded.
      const startHeight = `${detailsElement.offsetHeight}px`;
      const endHeight = `${summaryElement.offsetHeight + detailsContent.offsetHeight}px`;

      // Trigger the animation
      const expandAnimation = detailsElement.animate({
        height: [startHeight, endHeight]
      }, {
        duration: DURATION,
        easing: 'ease'
      });

      // Remove the fixed height inline style when the animation is complete
      expandAnimation.onfinish = () => {
        detailsElement.style.height = '';
      };
    });
  }

  const collapse = (detailsElement: HTMLDetailsElement, summaryElement: HTMLElement) => {
    // Get the current startHeight of the <details> element, then calculate the endHeight
    // as if it were collapsed.
    const startHeight = `${detailsElement.offsetHeight}px`;
    const endHeight = `${summaryElement.offsetHeight}px`;

    // Trigger the animation
    const collapseAnimation = detailsElement.animate({
      height: [startHeight, endHeight]
    }, {
      duration: DURATION,
      easing: 'ease'
    });

    // Close the <details> element when the animation is complete.
    collapseAnimation.onfinish = () => {
      detailsElement.open = false;
    };
  }

  const allCategoriesClassNames = classNames({
    "is-active": navigationState.focusedCatalogComponent.length <= 1
  })

  const onSummaryClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    const summaryElement = e.currentTarget as HTMLElement;
    const detailsElement = summaryElement.parentElement as HTMLDetailsElement;

    if (e.currentTarget != e.target) return;

    if (detailsElement.open) {
      collapse(detailsElement, summaryElement);
    } else {
      expand(detailsElement, summaryElement);
    }
  }, [])

  const focusAnchorElement = useCallback((anchorElement: HTMLAnchorElement) => {
    const navParent = anchorElement.closest("nav") as HTMLElement;
    const allDetailsElements = navParent.getElementsByTagName("details");

    Array.from(allDetailsElements).forEach(detailsElement => {
      const summaryElement = detailsElement.querySelector('summary') as HTMLElement;

      // If it isn't already open, expand the <details> element for the selected link.
      // Otherwise, close all other <details> elements except for the one just selected.
      if (!detailsElement.open && detailsElement.contains(anchorElement)) {
        expand(detailsElement, summaryElement);
      } else if (!detailsElement.contains(anchorElement)) {
        collapse(detailsElement, summaryElement);
      }
    });
  }, [])

  useEffect(() => {
    const focusedCategoryPath = focusedCategory ? focusedCategory.encodedKeyPath() : "all-categories";

    // Find both the desktop/mobile browser bodies
    const categoryBrowserBodies: Element[] = []
    if (desktopContainer) categoryBrowserBodies.push(desktopContainer);
    if (mobileBrowserRef.current) categoryBrowserBodies.push(mobileBrowserRef.current);

    // Find the anchor elements for the corresponding catalog component
    const focusableAnchorElements = categoryBrowserBodies.map((body) => {
      return body.querySelector(`[data-category="${focusedCategoryPath}"]`) as HTMLAnchorElement;
    }).filter(a => a);

    // Focus both of them
    focusableAnchorElements.forEach(focusAnchorElement);
  }, [desktopContainer, navigationState.focusedCatalogComponent, focusAnchorElement, focusedCategory])

  const onAnchorClick = useCallback((e: React.MouseEvent, category: Category | undefined) => {
    e.preventDefault();

    navToCategory(category)
  }, [navToCategory]);

  const categoryTree = useCallback((category: Category) => {
    const categoryClasses = classNames({
      "is-active": focusedCategory?.keyPath() == category.keyPath()
    });

    if (category instanceof CategoryBranch) {
      if (config.hideAgeRestrictedItems && category.onlyContainsAgeRestrictedItems()) return null;

      return (
        <li key={category.keyPath()}>
          <details>
            <summary onClick={onSummaryClick}>
              <a data-category={category.encodedKeyPath()} className={categoryClasses} onClick={e => onAnchorClick(e, category)}>{category.title}</a>
            </summary>

            <ul>
              {
                category.categories.map((category) => {
                  return categoryTree(category);
                })
              }
            </ul>
          </details>
        </li>
      );
    } else {
      if (config.hideAgeRestrictedItems && category.onlyContainsAgeRestrictedItems()) return null;

      return (
        <li key={category.encodedKeyPath()}>
          <a data-category={category.encodedKeyPath()} className={categoryClasses} onClick={(e) => onAnchorClick(e, category)}>{category.title}</a>
        </li>
      );
    }
  }, [focusedCategory, onSummaryClick, onAnchorClick, config.hideAgeRestrictedItems]);

  const catalogTree = useMemo(() => {
    return catalog.sectionsWithValidCategories(config).map((section) => categoryTree(section.categories[0]))
  }, [catalog, config, categoryTree]);

  const catalogBrowserBody =
    <ul>
      <li>
        <a data-category={"all-categories"} onClick={e => onAnchorClick(e, undefined)} className={allCategoriesClassNames}>All products</a>
      </li>
      {catalogTree}
    </ul>

  return <div className="app-view" id="categories">
    <header className="container has-divider has-high-contrast">
      {config.storefrontName}
    </header>

    <section className="scrollable-area container has-space-top">
      <nav className="tree-nav" ref={mobileBrowserRef}>
        {catalogBrowserBody}
      </nav>
    </section>

    {
      desktopContainer ? createPortal(
      <>
        {catalogBrowserBody}

        <section className="is-stuck-bottom has-gradient flex-grow-0">
          <button className="button is-full-width" data-action="click->visibility#hide" data-visibility-target-name="category-browser">Done</button>
        </section>
      </>, desktopContainer) : null
    }
  </div>;
}
